import React, { Component } from "react";
import { Modal, Form, Button, Table, Space, message, Typography, Tag, Divider, Card } from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import API from "../scripts/api";
import { EditOutlined, SaveOutlined, UserOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

class UserExperienceForm extends Component {
    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.editorInstance = null;
        this.state = {
            visible: false,
            currentUser: null,
            data: [],
            loading: false,
            submitting: false,
            editingId: null,
            analyzing: false,
            aiAnalysis: null
        };
    }

    show = (user) => {
        
        this.setState({ visible: true, currentUser: user , aiAnalysis: null});
        this.fetchList(user.id);
    };

    hide = () => {
        this.setState({ visible: false, editingId: null, aiAnalysis: null });
        this.formRef.current.resetFields();
        this.editorInstance.setData("");
    };

    fetchList = async (userId) => {
        this.setState({ loading: true });
        try {
            const result = await API.userExperience.list({ user_id: userId });
            this.setState({ data: result.data.data || result.data || [], loading: false });
        } catch (error) {
            this.setState({ loading: false });
        }
    };

    handleSubmit = async () => {
        try {
            const values = await this.formRef.current.validateFields();
            this.setState({ submitting: true });
            const payload = { ...values, user_id: this.state.currentUser.id };

            if (values.id) {
                await API.userExperience.update(values.id, payload);
                message.success("ویرایش با موفقیت انجام شد.");
            } else {
                await API.userExperience.store(payload);
                message.success("ثبت با موفقیت انجام شد.");
            }
            
            this.formRef.current.resetFields();
            this.editorInstance.setData("");
            this.setState({ editingId: null });
            this.fetchList(this.state.currentUser.id);
        } catch (error) {
            if (error.errorFields) {
                // خطا از سمت اعتبارسنجی فرم است
            } else {
                message.error("خطا در برقراری ارتباط با سرور.");
            }
        } finally {
            this.setState({ submitting: false });
        }
    };

    renderFullName = (user) => {
        if (!user) return "-";
        return `${user.name || ""} ${user.family || ""}`.trim() || "-";
    };

    analyzeWithAI = async () => {
        const { currentUser, data } = this.state;

        if (!currentUser?.id || !data?.length) {
            message.warning("تجربه‌ای برای تحلیل وجود ندارد.");
            return;
        }

        this.setState({ analyzing: true });

        try {
            const response = await API.userExperience.analyze({
                user_id: currentUser.id
            });

            this.setState({
                aiAnalysis: response.analysis || response.data?.analysis || null
            });

            message.success("تحلیل تجربه با موفقیت انجام شد.");
        } catch (error) {
            message.error("خطا در تحلیل تجربه.");
        } finally {
            this.setState({ analyzing: false });
        }
    };


    render() {
        const { visible, loading, submitting, data, editingId, currentUser } = this.state;

        const columns = [
            { 
                title: "توضیحات", 
                dataIndex: "description", 
                key: "description",
                render: (text) => (
                    <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#444' }} 
                         dangerouslySetInnerHTML={{ __html: text || "-" }} />
                ) 
            },
            { 
                title: "ثبت کننده", 
                dataIndex: "creator", 
                key: "creator",
                width: 150,
                render: (user) => <Text type="secondary">{this.renderFullName(user)}</Text>
            },
            { 
                title: "کاربر", 
                dataIndex: "user", 
                key: "user",
                width: 150,
                render: (user) => <Tag color="blue">{this.renderFullName(user)}</Tag>
            },
            { 
                title: "تاریخ ثبت", 
                dataIndex: "created_at", 
                key: "created_at",
                width: 120,
                render: (date) => date ? new Date(date).toLocaleDateString('fa-IR') : "-" 
            },
            { 
                title: "عملیات", 
                key: "action",
                width: 100,
                render: (_, record) => (
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => {
                            this.formRef.current.setFieldsValue({ id: record.id, description: record.description });
                            this.editorInstance.setData(record.description);
                            this.setState({ editingId: record.id });
                            // اسکرول به بالا برای ویرایش راحت‌تر
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    >
                        ویرایش
                    </Button>
                )
            }
        ];

        return (
            <Modal
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Title level={4} style={{ margin: 0 }}>مدیریت تجربیات</Title>
                        {currentUser && (
                            <Tag icon={<UserOutlined />} color="geekblue" style={{ margin: 0, padding: '4px 12px' }}>
                                کاربر: {this.renderFullName(currentUser)}
                            </Tag>
                        )}
                    </div>
                }
                open={visible}
                onCancel={this.hide}
                footer={null}
                width="95vw"
                style={{ top: 20 }}
                bodyStyle={{ height: "calc(100vh - 100px)", overflow: "auto", padding: '24px' }}
                destroyOnClose={true}
            >
                {/* بخش فرم */}
                <Card 
                    title={<Text strong>ثبت یا ویرایش تجربه جدید</Text>}
                    style={{ marginBottom: 24, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                    <Form layout="vertical" ref={this.formRef}>
                        <Form.Item name="id" hidden><input /></Form.Item>
                        <Form.Item 
                            label={<Text strong>محتوای تجربه</Text>} 
                            name="description" 
                            rules={[{ required: true, message: 'لطفا توضیحات را وارد کنید' }]}
                        >
                            <div style={{ 
                                border: '1px solid #d9d9d9', 
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                transition: 'all 0.3s'
                            }}>
                                <CKEditor 
                                    editor={ClassicEditor} 
                                    onReady={editor => {
                                        this.editorInstance = editor;
                                        editor.editing.view.change(writer => {
                                            writer.setStyle('min-height', '250px', editor.editing.view.document.getRoot());
                                        });
                                    }} 
                                    onChange={(e, editor) => this.formRef.current.setFieldsValue({ description: editor.getData() })}
                                />
                            </div>
                        </Form.Item>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <Button onClick={this.hide} disabled={submitting}>انصراف</Button>
                            <Button 
                                type="primary" 
                                icon={editingId ? <SaveOutlined /> : <EditOutlined />} 
                                loading={submitting} 
                                onClick={this.handleSubmit}
                                style={{ borderRadius: '6px', padding: '0 30px' }}
                            >
                                {editingId ? "بروزرسانی" : "ثبت تجربه"}
                            </Button>

                               <Button
                                    onClick={this.analyzeWithAI}
                                    loading={this.state.analyzing}
                                    disabled={!data?.length}
                                    style={{
                                        borderRadius: '6px',
                                        padding: '0 24px',
                                        background: '#f0f5ff',
                                        borderColor: '#adc6ff',
                                        color: '#1d39c4'
                                    }}
                                >
                                    تحلیل تجربه
                                </Button>
                        </div>
                    </Form>
                </Card>

                 {this.state.aiAnalysis && (
                    <Card
                        title="نتیجه تحلیل تجربه"
                        style={{
                            marginTop: 16,
                            borderRadius: 12,
                            background: '#fafafa'
                        }}
                    >
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>
                            {this.state.aiAnalysis}
                        </div>
                    </Card>
                )}
                
                <Divider orientation="right">لیست تجربیات ثبت شده</Divider>
                {/* بخش جدول */}
                <Table 
                    pagination={{ pageSize: 5 }} 
                    rowKey="id" 
                    loading={loading} 
                    dataSource={data} 
                    size="middle"
                    bordered
                    scroll={{ x: 800 }}
                    columns={columns} 
                />
            </Modal>
        );
    }
}

export default UserExperienceForm;
